import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Button } from 'reactstrap';

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import Pagination from '~/components/Pagination';
import PageHeader from '~/components/Common/PageHeader';
import SearchBox from '~/components/SearchBox';
import DropdownFilter from '~/components/DropdownFilter';
import DeleteModal from '~/components/Modals/DeleteModal';
import Loading from '~/components/Loading';
import history from '~/services/history';
import { resetSearch, setSearch } from '~/store/modules/search/actions';
import PartnerApplicationCard from './PartnerApplicationCard';

const environmentOptions = [
  { value: 'test', label: 'Teste' },
  { value: 'live', label: 'Produção' },
];

const statusOptions = [
  { value: 'active', label: 'Ativa' },
  { value: 'disabled', label: 'Desativada' },
  { value: 'revoked', label: 'Revogada' },
];

const PartnerApplications = () => {
  const dispatch = useDispatch();

  const [applications, setApplications] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);

  const [environmentFilter, setEnvironmentFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const searchQuery = useSelector(({ search }) => search.query);
  const [applicationToBeDeleted, setApplicationToBeDeleted] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const getURL = useMemo(() => 'administrator/partner-applications', []);

  const deleteURL = useMemo(
    () => `${getURL}/${applicationToBeDeleted?.id}`,
    [getURL, applicationToBeDeleted]
  );

  const params = useMemo(() => {
    return {
      page: pagination?.current_page > 1 ? pagination?.current_page : null,
      search: searchQuery || null,
      environment: environmentFilter || null,
      status: statusFilter || null,
    };
  }, [pagination, searchQuery, environmentFilter, statusFilter]);

  const setData = data => {
    const list = (data.data || []).map(app => ({
      ...app,
      formatted_created_at: app.created_at
        ? format(parseISO(app.created_at), "dd/MMM/yy - HH'h'mm", {
            locale: ptBR,
          })
        : '-',
      formatted_last_used_at: app.last_used_at
        ? format(parseISO(app.last_used_at), "dd/MMM/yy - HH'h'mm", {
            locale: ptBR,
          })
        : 'Nunca usada',
    }));

    setApplications(list);
    setPagination(data.meta);
  };

  useEffect(() => {
    dispatch(resetSearch());
    document.title = 'Aplicações Parceiras | Viddy';
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container className="container new-layout wide">
        <PageHeader
          pageTitle="APLICAÇÕES PARCEIRAS"
          responsivePosition="start"
          desktopPosition="start"
        />

        <Row className="w-100 mx-0 mb-2 mb-lg-4">
          <Col lg={12} className="px-0 mb-4 my-lg-0 d-lg-none">
            <SearchBox
              url={getURL}
              query={searchQuery}
              setQuery={q => dispatch(setSearch({ query: q }))}
              params={params}
              setData={setData}
              setLoading={setLoading}
            />
          </Col>
        </Row>

        <Row className="w-100 mx-0 mb-4 d-flex flex-row align-items-center">
          <Col className="px-0 order-2 order-lg-1" lg={8} align="start">
            <div className="d-flex flex-row align-items-baseline flex-wrap">
              <span className="text-left mt-4 mt-lg-0 d-inline text-muted font-size-14 mr-2">
                Filtros
              </span>
              <DropdownFilter
                name="environment-filter"
                id="environment-filter"
                placeholder="Ambiente"
                options={environmentOptions}
                url={getURL}
                params={params}
                setData={setData}
                setLoading={setLoading}
                filter={environmentFilter}
                setFilter={setEnvironmentFilter}
                query={searchQuery}
                setQuery={q => dispatch(setSearch({ query: q }))}
                clearable
                additionalClassName="mx-0 mr-2"
                width={165}
              />
              <DropdownFilter
                name="status-filter"
                id="status-filter"
                placeholder="Status"
                options={statusOptions}
                url={getURL}
                params={params}
                setData={setData}
                setLoading={setLoading}
                filter={statusFilter}
                setFilter={setStatusFilter}
                query={searchQuery}
                setQuery={q => dispatch(setSearch({ query: q }))}
                clearable
                additionalClassName="mx-0"
                width={165}
              />
            </div>
          </Col>

          <Col
            className="px-0 order-1 order-lg-2 mb-2 mt-2 mt-lg-0 mb-lg-0 pl-lg-3"
            lg={4}
            align="end"
          >
            <Button
              className="new-layout-btn main btn btn-primary waves-effect waves-light w-100 text-nowrap"
              color="primary"
              onClick={() => history.push('/aplicacoes-parceiras/registrar')}
            >
              <i className="las la-plus font-size-16 align-middle mr-2" />
              Nova Aplicação
            </Button>
          </Col>
        </Row>

        {loading ? (
          <Loading />
        ) : (
          <>
            {applications && applications.length > 0 ? (
              <>
                <Row className="w-100 mx-0 mb-4 d-none d-lg-flex px-3">
                  <Col xs={3} align="start">
                    <div className="new-layout-table-column-label">Nome</div>
                  </Col>
                  <Col xs={3} align="start">
                    <div className="new-layout-table-column-label">
                      Client ID
                    </div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">
                      Ambiente
                    </div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">Status</div>
                  </Col>
                  <Col xs={2} align="start">
                    <div className="new-layout-table-column-label">
                      Último uso
                    </div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">Criado</div>
                  </Col>
                  <Col xs={1} align="center">
                    <div className="new-layout-table-column-label">Ações</div>
                  </Col>
                </Row>

                {applications.map(application => (
                  <Row className="w-100 mx-0" key={application.id}>
                    <Col className="px-0" xs={12}>
                      <PartnerApplicationCard
                        application={application}
                        onDelete={incoming => {
                          setApplicationToBeDeleted(incoming);
                          setShowDeleteModal(true);
                        }}
                      />
                    </Col>
                  </Row>
                ))}

                <Row className="w-100 mx-0 mb-4">
                  <Col className="px-0" align="end">
                    {pagination && (
                      <div className="mb-3">
                        <Pagination
                          paginationMeta={pagination}
                          url={getURL}
                          setData={setData}
                          setLoading={setLoading}
                          params={params}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
              </>
            ) : (
              <h3>Nenhuma aplicação parceira encontrada.</h3>
            )}
          </>
        )}
      </Container>

      <DeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        deleteUrl={deleteURL}
        getUrl={getURL}
        params={params}
        setData={setData}
        itemsArray={applications}
        setItemToBeDeleted={setApplicationToBeDeleted}
        headerText="Deseja mesmo excluir a aplicação?"
        bodyText="Essa ação irá excluir a aplicação parceira e revogar todas as credenciais."
        itemName={applicationToBeDeleted?.name}
        successMsg="Aplicação excluída com sucesso!"
        btnText="Excluir"
      />
    </div>
  );
};

export default PartnerApplications;
